import { createFileRoute, notFound, useNavigate } from '@tanstack/react-router';
import { Button } from 'primereact/button';
import { useEffect, useState } from 'react';
import { z } from 'zod';
import {
  endpointPortfolioCompanyGET,
  PortfolioCompanyResponse,
} from '~/api/def/endpoints/portfolioCompanyEndpoint';
import { Breadcrumb } from '~/components/ui/layout/navigation/breadcrumb/Breadcrumb';
import { HeaderContainer } from '~/components/ui/layout/page/HeaderContainer';
import { getPortfolioLinkParams } from '~/components/ui/tables/ag/portfolios/PortfolioLinkCell';
import { PortfoliosTable } from '~/components/ui/tables/ag/portfolios/portfoliosTable';
import { canAccess } from '~/utils/rbac';
import { useRequireFeatureFlag } from '~/hooks/useRequireFeatureFlag';
import { FEATURE_FLAGS } from '~/config/orgConfigConstants';

const companiesListSearchSchema = z.object({
  //fund name is used for path - the portfolio company name is used for the breadcrumb
  fundName: z.string().optional(),
  //generally we should be in context a fund here
  //optimally we should extract fund ID fund name from portfolio data
  fundId: z.string().optional(),
});

function FundPortfolioCompanies() {
  const navigate = useNavigate();
  const { fundId } = Route.useParams();
  const { fundName } = Route.useSearch();
  const [portfolioCompanyList, setPortfolioCompanyList] = useState<
    PortfolioCompanyResponse[]
  >([]);
  useEffect(() => {
    endpointPortfolioCompanyGET(fundId)
      .then((companies) => {
        setPortfolioCompanyList(
          companies.map((pc) => ({
            ...pc,
            createdAt: pc.createdAt ? new Date(pc.createdAt) : '',
            incorporationDate: pc.incorporationDate
              ? new Date(pc.incorporationDate)
              : '',
          })),
        );
      })
      .catch((err) => {
        console.log(err);
      });
  }, [fundId]);

  return (
    <div
      style={{
        height: '100vh',
        width: '100%',
      }}
    >
      <div className="h-4rem">
        <HeaderContainer>
          <Breadcrumb
            items={[
              { name: `Funds`, url: '/fund/overview' },
              ...(fundName
                ? [{ name: fundName, url: `/fund/overview?fundId=${fundId}` }]
                : []),
              { name: 'Portfolio Companies' },
            ]}
          />
        </HeaderContainer>
      </div>
      <h3 className="ml-4 mt-3">{`${fundName ? 'Portfolio Companies: ' + fundName : 'All'}`}</h3>
      <div
        className="pb-4 pl-4"
        style={{
          height: 'calc(100% - 4rem)',
        }}
      >
        <div className="flex justify-between h-3rem">
          <Button
            onClick={() =>
              navigate({
                to: `/portfolio/create${getPortfolioLinkParams({ fundId, fundName })}`,
              })
            }
            className="mb-3"
          >
            Create Company
          </Button>
        </div>
        <div
          style={{
            height: 'calc(100% - 3rem)',
          }}
        >
          <PortfoliosTable
            rowData={portfolioCompanyList}
            fundName={fundName}
            fundId={fundId}
            loading={false}
          />
        </div>
      </div>
    </div>
  );
}

const FundPortfolioCompaniesRoute = () => {
  const { allowed } = useRequireFeatureFlag(FEATURE_FLAGS.FUND_MANAGEMENT);
  if (!allowed) return null;
  return <FundPortfolioCompanies />;
};

export const Route = createFileRoute(
  '/_authLayout/fund/portfolioCompanies/$fundId',
)({
  beforeLoad: async () => {
    if (!canAccess('fund')) {
      throw notFound();
    }
  },
  component: FundPortfolioCompaniesRoute,
  validateSearch: companiesListSearchSchema,
});
